import React, { useContext, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { Text, Button, Toast, FlexContainer, ErrorLabel, ImageCropModal, StandardModal, StandardModalActionGroup, portalColors } from "src/components";
import { UserContext } from "src/scenes/App/UserContext";
import { accountOwnerEmployeeName, generateNameInitials } from "src/utils/helpers";
import PencilIcon from "src/img/new/chats/pencil.svg";
import PlusIcon from "src/img/new/chats/plus.svg";
import { UserKeys } from "src/constants/userDetails";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";
import { FEATURE_FLAGS } from "src/constants/FeatureFlag";
import { ACCOUNT_OWNER_PERMISSION } from "src/constants/permissions";

const Icon = styled.img`
    height: ${p => p.height};
    width: ${p => p.width};
    color: ${p => p.color};
    cursor: pointer;
`;

const StyledInputLikeDiv = styled.div`
    border: 0.0625rem solid ${portalColors.border};
    width: 100%;
    max-width: 14.25rem;
    line-height: 2.5rem;
    text-align: center;
    background: ${portalColors.surface};
    color: ${portalColors.text};
    font-weight: 700;
    font-size: 1rem;
    border-radius: 0.4375rem;
    margin: 0 0 0.4375rem 0;
    :focus {
        outline: 0;
    }
`;

const ProfilePhoto = styled.div`
    position: relative;
    height: 7.875rem;
    width: 7.875rem;
    border-radius: 50%;
    border: 0.25rem solid ${portalColors.surface};
    box-shadow: ${portalColors.shadow};
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 3.125rem;
    font-weight: bold;
    color: #484848;
    background: #D6D6D6;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    margin: 0 0 2.5rem 0;
`;

const EditPhotoButton = styled.div`
    position: absolute;
    height: 1.9375rem;
    width: 1.9375rem;
    border-radius: 50%;
    background: ${portalColors.surfaceTertiary};
    border: 0.125rem solid ${portalColors.surface};
    bottom: 0;
    transform: translateY(55%);
    box-shadow: ${portalColors.shadow};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    & * {
        cursor: pointer;
    }
`;

const ClearPhotoButton = styled.div`
    position: absolute;
    height: 1.9375rem;
    width: 1.9375rem;
    border-radius: 50%;
    background: ${portalColors.surface};
    box-shadow: ${portalColors.shadow};
    top: 0;
    right: 0;
    transform: translate(30%, -40%);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LeadershipRoleSection = styled.div`
    display: flex;
    flex-direction: row;
    width: 80%;
    justify-content: center;
    flex-wrap: wrap;
    margin: 0.625rem 0 1.125rem 0;
`;

const LeadershipRoleItem = styled.div`
    border-radius: 0.4375rem !important;
    background-color: #e5fff6;
    color: #65a48f;
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 2.5rem;
    padding: 0.5rem 1rem;
    margin: 0.5rem 0.5rem;
`;

const ProfileSettingsModal = (props) => {
    const userContext = useContext(UserContext);
    const { appFns } = userContext;
    const { firstName, lastName, token, email, isClient, employmentPosition, department, profilePictureUrl } = props.userToShow;
    const { handleClose, profileModalUserLeadershipRoles } = props;
    const hasAccountOwnerEmployeeName =
        Boolean(accountOwnerEmployeeName.firstName) || Boolean(accountOwnerEmployeeName.lastName);
    const shouldUseAccountOwnerEmployeeName =
        props.isCurrentUser &&
        userContext?.[UserKeys.PERMISSIONS]?.includes(ACCOUNT_OWNER_PERMISSION) &&
        hasAccountOwnerEmployeeName;
    const displayFirstName = shouldUseAccountOwnerEmployeeName ? accountOwnerEmployeeName.firstName : firstName;
    const displayLastName = shouldUseAccountOwnerEmployeeName ? accountOwnerEmployeeName.lastName : lastName;
    
    const originalData = {
        profilePictureUrl: profilePictureUrl
    };

    const [formData, setFormData] = useState({ ...originalData });
    const [upload, setUpload] = useState({
        isUploading: false,
        timestamp: "",
        uploadPercentage: 0,
        fullUrl: "",
        fileToUpload: null,
        newImageUrl: null
    });
    const [errors, setErrors] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const headers = { headers: { Authorization: "Bearer " + token } };
    // Crop image
    const [imageToCrop, setImageToCrop] = useState(null);
    const [showCropperModal, setShowCropperModal] = useState(false);
        
    const featureFlags = userContext?.featureFlags || [];

    const canSeeLeadershipRoles =
        featureFlags.includes(FEATURE_FLAGS.BUSINESS_CHAT);

    const hasLeadershipRoles =
        Array.isArray(profileModalUserLeadershipRoles) &&
        profileModalUserLeadershipRoles.length > 0;

    const shouldShowLeadershipRoles =
        canSeeLeadershipRoles && hasLeadershipRoles;
    
    const handleCropComplete = (file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);

        if (upload?.readerResult) {
            URL.revokeObjectURL(upload.readerResult); 
        }
    
        setShowCropperModal(false);

        setUpload(prev => ({
            ...prev,
            newImageUrl: url,
            fileToUpload: file, 
        }));
    };

    const handleProfileUpdate = (e) => {
        const fileToUpload = e.target.files[0];
        const validImageTypes = ["image/jpeg", "image/png"];
        if (!validImageTypes.includes(fileToUpload.type)) {
            Toast.error("Unsupported file type. Images may be JPEG or PNG format.");
            return;
        }
        
        setIsLoading(true);
        setErrors([]);
        // Open cropper modal with the file
        setImageToCrop(fileToUpload);
        setShowCropperModal(true);

        let fileExtension = fileToUpload.name.split(".").pop();
        axios.get(`${API_URL}/chats/profile/generatePhotoUrl/${fileExtension}`, headers)
            .then(res => {
                const fullUrl = res.data.url;
                const storedUrl = fullUrl.substring(0, fullUrl.indexOf("?"));

                setFormData({
                    ...formData,
                    profilePictureUrl: storedUrl
                });

                setUpload({
                    ...setUpload,
                    fullUrl: fullUrl,
                    fileToUpload: fileToUpload,
                    // creating a url here to give a preview of the selected image
                    // since image wont be uploaded until form submission
                    newImageUrl: URL.createObjectURL(fileToUpload)
                });
            })
            .catch(err => {
                const message = err.getApiErrorDetails().message;
                setErrors(message);
                return Toast.error(message);
            }).finally(() => {
                setIsLoading(false);
            });
    };

    const handleFormSubmit = async () => {
        setErrors([]);
        if (!props.isCurrentUser) {
            return Toast.error("You cannot update the profile of another user.");
        }
        setIsLoading(true);
        uploadFileAndSaveProfile().catch(() => {
            const message = "Failed to upload image to the server.";

            setErrors(message);
            Toast.error(message);
        });
    };

    const uploadFileAndSaveProfile = async () => {
        try {
            if (upload.fileToUpload !== null) {
                const parameters = {
                    headers: { "Content-Type": upload.fileToUpload.type },
                    onUploadProgress: progressEvent => {
                        let uploadPercent = (progressEvent.loaded / progressEvent.total) * 100;
    
                        setUpload({
                            isUploading: true,
                            uploadPercentage: uploadPercent.toFixed(2),
                        });
                    }
                };
                await axios.put(upload.fullUrl, upload.fileToUpload, parameters);
            }   
            

            const response = await axios.put(
                `${API_URL}/chats/profile/updateEmployeeProfilePicture`, formData, headers
            );
            appFns.updateUserContextProperties({
                [UserKeys.PROFILE_PICTURE_URL]: response.data.profilePictureUrl
            });

            Toast.success("Profile successfully updated.");
            setUpload({
                ...upload,
                newImageUrl: null,
                fileToUpload: null,
                fullUrl: ""
            });
            // clean up the blob url created after selecting an image to upload
            URL.revokeObjectURL(upload.newImageUrl);
        } catch (err) {
            const message = err.getApiErrorDetails().message;
            Toast.error(message);
        } finally {
            setIsLoading(false);
            handleClose();
        }
    };

    const handleClearProfilePicture = () => {
        setFormData({
            ...formData,
            profilePictureUrl: ""
        });
    };

    /**
     * @returns {string} - a valid CSS background-image value; "none" or "url(...)"
     */
    const renderUserImage = () => {
        return upload.newImageUrl !== null ? `url(${upload.newImageUrl})`
            : !formData.profilePictureUrl ? "none" : `url(${formData.profilePictureUrl})`;
    };

    const renderErrors = () => errors.map((err, index) => <ErrorLabel key={index}>{ err }</ErrorLabel>);
    const hasProfileChanged = () => {
        if (originalData.profilePictureUrl !== formData.profilePictureUrl)
            return true;

        return false;
    };

    return (
        <React.Fragment>
            <StandardModal
                open={true}
                onClose={handleClose}
                title={props.isCurrentUser ? "Profile Settings" : "User Profile"}
                widthMode="large"
                footer={(
                    <StandardModalActionGroup $pushRight>
                        <Button
                            onClick={handleClose}
                            size="0.875rem"
                            border="1px solid var(--calendar-text, #171A22)"
                            color="var(--calendar-text, #171A22)"
                            width="8.75rem"
                            style={{ borderRadius: "7px", boxShadow: "none" }}
                        >
                            Close
                        </Button>
                        {props.isCurrentUser && (
                            <Button
                                backgroundColor="var(--calendar-action-primary-bg, #5B6EF5)"
                                color="#FFF"
                                width="8.75rem"
                                border="1px solid var(--calendar-action-primary-bg, #5B6EF5)"
                                style={{ borderRadius: "7px", boxShadow: "none", opacity: !hasProfileChanged() && "0.4" }}
                                onClick={() => hasProfileChanged() ? handleFormSubmit() : null}
                            >
                                {isLoading ? "Saving..." : "Save Changes"}
                            </Button>
                        )}
                    </StandardModalActionGroup>
                )}
            >
                <div style={{ 
                    display: "flex", 
                    flexDirection: "column", alignItems: "center"
                }}>
                    <Text margin="2.3125rem 0 1.25rem 0" weight="700" size="1.25rem">
                        { props.isCurrentUser ? "My " : "" }Profile
                    </Text>
                    <ProfilePhoto
                        style={{
                            backgroundImage: renderUserImage()
                        }}
                    >
                        { !formData.profilePictureUrl ? generateNameInitials(displayFirstName, displayLastName) : "" }
                        { props.isCurrentUser &&
                            <React.Fragment>
                                {renderUserImage() !== "none" && <ClearPhotoButton
                                    onClick={() => handleClearProfilePicture()}
                                >
                                    <Icon width="1.1rem" height="1.1rem" src={PlusIcon} style={{ transform: "rotate(45deg)" }} />
                                </ClearPhotoButton>}
                                <EditPhotoButton>
                                    <input type="file" style={{ opacity: 0, position: "absolute" }} 
                                        onChange={e => handleProfileUpdate(e)}
                                    />
                                    <Icon width="1.1rem" height="1.1rem" src={PencilIcon} />
                                </EditPhotoButton>
                            </React.Fragment>
                        }
                        
                    </ProfilePhoto>
                    { upload.isUploading &&
                        <FlexContainer>
                            <Text>Uploading - { upload.uploadPercentage }%</Text>
                        </FlexContainer>
                    }

                    <StyledInputLikeDiv>{displayFirstName}</StyledInputLikeDiv>
                    <StyledInputLikeDiv>{displayLastName}</StyledInputLikeDiv>
                    
                    <Text margin="1.875rem 0 0 0" size="1rem" weight="700">Email</Text>
                    <Text margin="0.625rem 0 1.125rem 0" size="1rem" height="1.3125rem">{ email }</Text>

                    { !isClient &&
                        <React.Fragment>
                            {employmentPosition && 
                                <>
                                    <Text margin="0" size="1rem" weight="700">Job Title</Text>
                                    <Text margin="0.625rem 0 1.125rem 0" size="1rem" height="1.3125rem">{ employmentPosition }</Text>
                                </>
                            }

                            {department && 
                                <>
                                    <Text margin="0" size="1rem" weight="700">Department / Group</Text>
                                    <Text margin="0.625rem 0 1.125rem 0" size="1rem" height="1.3125rem">{ department }</Text>
                                </>
                            }
                        </React.Fragment>
                    }

                    { shouldShowLeadershipRoles && 
                        <>
                            <Text margin="0" size="1rem" weight="700">Leadership Roles</Text>
                            <LeadershipRoleSection>
                                { profileModalUserLeadershipRoles.map((lrItem) => 
                                    <LeadershipRoleItem key={lrItem.uuid} >
                                        { lrItem.name }
                                    </LeadershipRoleItem>
                                )}
                            </LeadershipRoleSection>
                        </>
                    }

                    { errors.length ? renderErrors() : "" }
                </div>
                <ImageCropModal
                    imageSrc={imageToCrop ? URL.createObjectURL(imageToCrop) : null}
                    isOpen={showCropperModal}
                    onClose={(shouldClear) => {
                        setShowCropperModal(false);
                        if (shouldClear) {
                            setImageToCrop(null);
                            if (upload.newImageUrl) {
                                URL.revokeObjectURL(upload.newImageUrl);
                            }
                            setUpload(prev => ({
                                ...prev,
                                newImageUrl: null,
                                fileToUpload: null,
                            }));
                            setFormData(prev => ({
                                ...prev,
                                profilePictureUrl: props.userToShow.profilePictureUrl || "",
                            }));
                        }
                    }}
                    onCropComplete={handleCropComplete}
                    aspectRatios={[
                        { width: 1, height: 1, label: "1:1", standardName: ASPECT_RATIO_LABEL.SQUARE }
                    ]}
                    allowedOrientations={["circle"]}
                    lockedAspectRatio={undefined}
                />
            </StandardModal>
        </React.Fragment>
    );
};

export default ProfileSettingsModal;
